import styled from 'foundations-theming/utils';
import { useEffect } from 'react';
import { renderToString } from 'react-dom/server';
import { css } from 'styled-components';
import { mediaQueries, NUM_GRID_BREAKPOINT_SMALL } from 'developer-docs-shared-components/utils/mediaQueries';
import { GYPSUM, OBSIDIAN, OLAF } from 'HubStyleTokens/colors';
import { Button } from 'foundations-components/transitional/components/Button';
import { jsx as _jsx } from "react/jsx-runtime";
const windowHasHbsptForms = window => window && 'hbspt' in window && 'forms' in window.hbspt && 'create' in window.hbspt.forms;
const attributesToPropsObject = attributes => {
  return attributes.split(' ').reduce((props, attribute) => {
    const [key, value] = attribute.split('=');
    if (key && value) {
      props[key] = value.replace(/"/g, ''); // remove the quotes around the value
    }
    return props;
  }, {});
};
const button = (props, content) => {
  return renderToString( /*#__PURE__*/_jsx(Button, Object.assign({
    use: "primary"
  }, props, {
    children: content
  })));
};
function HubSpotForm({
  form,
  targetId
}) {
  // replace the buttons and its context with the button component
  const inlineMessage = form.message.replace(/<ui-button([^>]*)>(.*?)<\/ui-button>/g, (match, group1, group2) => {
    return button(attributesToPropsObject(group1), group2);
  });
  useEffect(() => {
    const script = document.createElement('script');
    script.src = '//js.hsforms.net/forms/embed/v2.js';
    document.body.appendChild(script);
    script.addEventListener('load', () => {
      if (windowHasHbsptForms(window)) {
        window.hbspt.forms.create({
          portalId: '53',
          formId: form.formId,
          inlineMessage,
          target: `#${targetId}`
        });
      }
    });
  }, [form.formId, targetId, inlineMessage]);
  return /*#__PURE__*/_jsx("div", {
    id: targetId
  });
}
const formError = '#b50001';
const formInputBackground = GYPSUM;
const formInput = OBSIDIAN;
const formInputBorder = '#b6c7d6';
const submitButton = '#ff5c35';
/** * Copied relevant styles from the developers.hubspot.com form css https://developers.hubspot.com/wt-assets/static-files/4.0.6/styleguide/styles.css
 * Also, added some styles to make the form fit the design of the page better (like font-family: inherit) */

const submitButtonStyles = css(["font-family:inherit;text-align:center;border-radius:3px;transition:0.3s ease-in-out;display:block;text-decoration:none;line-height:1.57;background-color:", ";color:", ";padding:14px 22px;font-size:16px;appearance:none;border:0;font-weight:500;"], submitButton, OLAF);
const StyledDiv = styled.div.withConfig({
  displayName: "Form__StyledDiv",
  componentId: "sc-4vv02j-0"
})([".inputs-list label{font-family:inherit;}.hs-form .hs-error-msgs label{display:block;color:", ";font-size:14px;font-weight:500 !important;margin-top:8px !important;padding:0;font-family:inherit;}.hs-form label .hs-form-required{color:", ";margin-left:4px;font-family:inherit;}.hs-form label{cursor:pointer;display:block;}.hs-form textarea{box-sizing:border-box !important;}.hs-form .hs-input{font-size:16px;background-color:", ";color:", ";line-height:1.375;border:1px solid;border-color:", ";border-radius:3px;width:100%;padding:9px 10px;min-height:40px !important;transition:all 0.15s ease-out,visibility 0s;}.hs-form .hs-input:focus{box-shadow:0 0 4px 1px rgba(0,208,228,0.3),0 0 0 1px ", ";}.hs-form .hs-form-checkbox input,.hs-form .hs-form-booleancheckbox input{overflow:hidden;position:absolute;top:0;left:0;width:0px;height:0px;clip:rect(0,0,0,0);}.hs-form .hs-form-checkbox input:hover + span::before,.hs-form .hs-form-booleancheckbox input:hover + span::before{background-color:", ";}.hs-form .hs-form-checkbox input:checked + span::before,.hs-form .hs-form-booleancheckbox input:checked + span::before{border-color:#0fbfbf;}.hs-form .hs-form-checkbox input:checked + span::after,.hs-form .hs-form-booleancheckbox input:checked + span::after{border-color:#7691ad;}.hs-form .hs-form-checkbox input + span,.hs-form .hs-form-booleancheckbox input + span{position:relative;padding:2px 0 2px 32px;}.hs-form .hs-form-checkbox input + span::before,.hs-form .hs-form-checkbox input + span::after,.hs-form .hs-form-booleancheckbox input + span::before,.hs-form .hs-form-booleancheckbox input + span::after{content:'';display:inline-block;position:absolute;top:50%;transform:translateY(-50%);transition:all 0.15s ease-out;}.hs-form .hs-form-checkbox input + span::before,.hs-form .hs-form-booleancheckbox input + span::before{left:0;vertical-align:middle;border:2px solid #b6c7d6;width:16px;height:16px;border-radius:3px;}.hs-form .hs-form-checkbox input + span::after,.hs-form .hs-form-booleancheckbox input + span::after{background-color:transparent;left:6px;border-width:0 2px 2px 0;border-style:solid;border-color:transparent;transform:translateY(-7px) rotate(45deg);width:5px;height:10px;}", "{.hs-form .hs_submit .hs-button{width:100%;padding:22px 32px;font-size:17px;box-sizing:border-box;}}.hs-form .hs_submit .hs-button{", "}.hs-form .hs_submit .hs-button:hover{", " background-color:#ffcec2;color:", ";box-shadow:none;text-shadow:none;}.hs-form .hs_submit .hs-button:active{background-color:#d44325;color:", ";border:1px solid #d44325;}.hs-richtext{font-family:inherit !important;line-height:inherit !important;color:inherit !important;}.submitted-message{font-family:inherit !important;}"], formError, ({
  theme
}) => theme.color['do-not-use-slinky'], formInputBackground, formInput, formInputBorder, ({
  theme
}) => theme.color['do-not-use-focus-ring-base'], ({
  theme
}) => theme.color['do-not-use-gypsum'], mediaQueries.belowWidth(NUM_GRID_BREAKPOINT_SMALL), submitButtonStyles, submitButtonStyles, ({
  theme
}) => theme.color['do-not-use-olaf'], ({
  theme
}) => theme.color['do-not-use-olaf']);
const Form = ({
  formId,
  message
}) => {
  const targetId = `hsFormTarget__${formId}`;
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(StyledDiv, {
      id: targetId,
      children: /*#__PURE__*/_jsx(HubSpotForm, {
        form: {
          formId,
          message
        },
        targetId: targetId
      })
    })
  );
};
export default Form;